"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MissouriRules = void 0;
const unwrap_1 = require("../../unwrap");
const FEDERAL_1 = require("./FEDERAL");
const MO_DERIVATIONS = {
    effectiveWasteKind: {
        from: ["wasteKind", "managedAsUniversalWaste", "operatesUnderSubpartP"],
        label: "Governing program",
        citation: "40 CFR 273.9, 261.6(a)(4), 266.501",
        description: (0, unwrap_1.unwrap) `Missouri's incorporation by reference reaches parts 273 (universal waste),
      279 (used oil) and 266 subpart P (hazardous waste pharmaceuticals), and Missouri adopts
      the full federal five universal waste categories with no state-added category of its own.
      So which program governs a container is the federal question, answered the federal way.`,
        cases: [
            {
                when: { wasteKind: "UNIVERSAL_WASTE", managedAsUniversalWaste: true },
                produces: "UNIVERSAL_WASTE",
            },
            {
                when: { wasteKind: "UNIVERSAL_WASTE", managedAsUniversalWaste: false },
                produces: "ORDINARY",
            },
            { when: { wasteKind: "USED_OIL" }, produces: "USED_OIL" },
            {
                when: { wasteKind: "PHARMACEUTICAL", operatesUnderSubpartP: true },
                produces: "SUBPART_P_PHARMACEUTICAL",
            },
            {
                when: { wasteKind: "PHARMACEUTICAL", operatesUnderSubpartP: false },
                produces: "ORDINARY",
            },
            { when: { wasteKind: null }, produces: "ORDINARY" },
        ],
    },
};
/**
 * Spec, not snapshot (see the discipline header in ../types.ts): a row
 * changes only when a regulatory specialist says the law reads differently —
 * never to make the class below pass.
 */
/** The Missouri deviation: the one-year leg of the (2)(A)4 alternative. */
const MO_SATELLITE_STANDING_CLOCK = {
    kind: "TIME",
    days: 365,
    citation: "10 CSR 25-5.262(2)(A)4",
    jurisdiction: "MO",
};
const FEDERAL_SATELLITE_CAPS = {
    kind: "VOLUME",
    nonAcuteGal: 55,
    acuteGal: 0.25,
    acuteKg: 1,
    moveWindowDays: 3,
    citation: "40 CFR 262.15",
    jurisdiction: "FEDERAL",
};
const FEDERAL_VSQG_NO_LIMIT = {
    kind: "NO_LIMIT",
    citation: "40 CFR 262.14",
    jurisdiction: "FEDERAL",
};
const FEDERAL_NO_STANDING_SATELLITE_CLOCK = {
    kind: "NO_LIMIT",
    citation: "40 CFR 262.15",
    jurisdiction: "FEDERAL",
};
const FEDERAL_UNIVERSAL_WASTE_CLOCK = {
    kind: "TIME",
    days: 365,
    citation: "40 CFR 273.15(a), 273.35(a)",
    jurisdiction: "FEDERAL",
};
const FEDERAL_SUBPART_P_CLOCK = {
    kind: "TIME",
    days: 365,
    citation: "40 CFR 266.502(f)(1)",
    jurisdiction: "FEDERAL",
};
const FEDERAL_USED_OIL_NO_LIMIT = {
    kind: "NO_LIMIT",
    citation: "40 CFR 279.22",
    jurisdiction: "FEDERAL",
};
const MO_SATELLITE_NOTE = [
    (0, unwrap_1.unwrap) `
    The relevant MO customization is 10 CSR 25-5.262(2)(A)4 (https://www.sos.mo.gov/cmsimages/adrules/csr/current/10csr/10c25-5.pdf).
  `,
    (0, unwrap_1.unwrap) `
    This is an optional alternative to 40 CFR 262.15, applying to both LQGs and SQGs.
    The facility may choose for EACH SAA whether to use the federal or MO limit.
    The federal rule is a per-SAA limit; MO's alternative is a per-wastestream-per-SAA limit,
    so it is more permissive if you have multiple wastestreams at a given SAA.
    Currently, we do not support this per-SAA decision, we are simply putting BOTH the federal
    and MO limits on containers (which is over-strict rather than under-strict).
  `,
    (0, unwrap_1.unwrap) `
    After 365 days, you have another 3 days to comply with the requirement to move the waste
    out of the SAA; currently we only support this move-window concept for volume-based limits.
    Arguably we could implement by just making the limit 368 days, but that seems more confusing.
  `,
    (0, unwrap_1.unwrap) `
    A FULL container at an SAA using the MO alternative must be moved, even if that container's
    capacity is less than 55 gallons. Not currently supported.
  `,
    (0, unwrap_1.unwrap) `
    MO requires that if you elect to use its alternative, you have no more than 1 container per
    wastestream at any given SAA. Not currently supported.
  `,
    (0, unwrap_1.unwrap) `
    MO requires that at any given SAA you are using either the federal 262.15 regulations OR
    the MO alternative, never mixing them. Not currently supported.
  `,
].join("\n\n");
const MO_EXPECTATIONS = [
    // ── CENTRAL ACCUMULATION — federal part 262 incorporated ──────────────
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: { areaType: "CENTRAL", generatorStatus: "LQG" },
        expect: {
            clock: { kind: "TIME", days: 90, citation: "40 CFR 262.17(a)", jurisdiction: "FEDERAL" },
        },
    },
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: { areaType: "CENTRAL", generatorStatus: "SQG", mustTransport200Miles: false },
        expect: {
            clock: { kind: "TIME", days: 180, citation: "40 CFR 262.16(b)", jurisdiction: "FEDERAL" },
        },
    },
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: { areaType: "CENTRAL", generatorStatus: "SQG", mustTransport200Miles: true },
        expect: {
            clock: { kind: "TIME", days: 270, citation: "40 CFR 262.16(c)", jurisdiction: "FEDERAL" },
        },
    },
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: { areaType: "CENTRAL", generatorStatus: "VSQG" },
        expect: { clock: FEDERAL_VSQG_NO_LIMIT },
    },
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: { areaType: "CENTRAL", generatorStatus: null },
        expect: { clock: { kind: "UNDETERMINED" } },
    },
    // ── SATELLITE ACCUMULATION — the one Missouri departure ───────────────
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: { areaType: "SATELLITE", generatorStatus: "LQG" },
        expect: { caps: FEDERAL_SATELLITE_CAPS, clock: MO_SATELLITE_STANDING_CLOCK },
        note: MO_SATELLITE_NOTE,
    },
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: { areaType: "SATELLITE", generatorStatus: "SQG" },
        expect: { caps: FEDERAL_SATELLITE_CAPS, clock: MO_SATELLITE_STANDING_CLOCK },
        note: `(see above - applies to both LQGs and SQGs)`,
    },
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: { areaType: "SATELLITE", generatorStatus: "VSQG" },
        expect: { caps: FEDERAL_VSQG_NO_LIMIT, clock: FEDERAL_NO_STANDING_SATELLITE_CLOCK },
    },
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: { areaType: "SATELLITE", generatorStatus: null },
        expect: { caps: FEDERAL_SATELLITE_CAPS, clock: MO_SATELLITE_STANDING_CLOCK },
        note: "The more protective default until the status is resolved.",
    },
    // ── UNIVERSAL WASTE — federal part 273 incorporated ───────────────────
    // The Missouri satellite alternative is an alternative to 40 CFR 262.15,
    // which universal waste, used oil and subpart P pharmaceuticals are all
    // scoped out of — so none of them takes the one-year state clock.
    {
        given: { effectiveWasteKind: "UNIVERSAL_WASTE" },
        when: { areaType: "CENTRAL" },
        expect: { clock: FEDERAL_UNIVERSAL_WASTE_CLOCK },
    },
    {
        given: { effectiveWasteKind: "UNIVERSAL_WASTE" },
        when: { areaType: "SATELLITE" },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 261.9, 273.1(b)", jurisdiction: "FEDERAL" },
            clock: FEDERAL_UNIVERSAL_WASTE_CLOCK,
        },
    },
    // ── USED OIL — federal part 279 incorporated ──────────────────────────
    {
        given: { effectiveWasteKind: "USED_OIL" },
        when: { areaType: "CENTRAL" },
        expect: { clock: FEDERAL_USED_OIL_NO_LIMIT },
    },
    {
        given: { effectiveWasteKind: "USED_OIL" },
        when: { areaType: "SATELLITE" },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 261.6(a)(4)", jurisdiction: "FEDERAL" },
            clock: FEDERAL_USED_OIL_NO_LIMIT,
        },
    },
    // ── SUBPART P PHARMACEUTICALS — federal part 266 incorporated ─────────
    {
        given: { effectiveWasteKind: "SUBPART_P_PHARMACEUTICAL" },
        when: { areaType: "CENTRAL" },
        expect: { clock: FEDERAL_SUBPART_P_CLOCK },
    },
    {
        given: { effectiveWasteKind: "SUBPART_P_PHARMACEUTICAL" },
        when: { areaType: "SATELLITE" },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 266.501(d)", jurisdiction: "FEDERAL" },
            clock: FEDERAL_SUBPART_P_CLOCK,
        },
    },
];
/**
 * MISSOURI ACCUMULATION THRESHOLD EXPECTATIONS — 40 CFR 262.14, 262.16 as
 * 10 CSR 25-5.262 incorporates them.
 *
 * Missouri's figures are the federal ones, so the inherited slot already
 * answers correctly and this class overrides no threshold behavior. The table
 * is authored anyway rather than inherited, for the same reason Delaware's
 * is: a state's claims are what the specialist reviews and what the SME
 * packet prints under its heading, so inheriting would put federal rows
 * behind Missouri's name without anyone having read them for Missouri.
 */
const MO_ACCUMULATION_THRESHOLD_EXPECTATIONS = [
    {
        when: { generatorStatus: "LQG" },
        expect: {
            thresholds: { totalKg: null, nonAcuteKg: null, acuteKg: null },
            judgedAgainst: { abbreviation: "LQG", name: "Large Quantity Generator" },
        },
    },
    {
        when: { generatorStatus: "SQG" },
        expect: {
            thresholds: { totalKg: 6000, nonAcuteKg: null, acuteKg: null },
            judgedAgainst: { abbreviation: "SQG", name: "Small Quantity Generator" },
        },
    },
    {
        when: { generatorStatus: "VSQG" },
        expect: {
            thresholds: { totalKg: null, nonAcuteKg: 1000, acuteKg: 1 },
            judgedAgainst: { abbreviation: "VSQG", name: "Very Small Quantity Generator" },
        },
    },
    {
        when: { generatorStatus: null },
        expect: { thresholds: { totalKg: null, nonAcuteKg: null, acuteKg: null }, judgedAgainst: null },
    },
];
function effectiveMoWasteKind(ctx) {
    if (ctx.wasteKind === "UNIVERSAL_WASTE" && ctx.managedAsUniversalWaste) {
        return "UNIVERSAL_WASTE";
    }
    if (ctx.wasteKind === "USED_OIL") {
        return "USED_OIL";
    }
    if (ctx.wasteKind === "PHARMACEUTICAL" && ctx.operatesUnderSubpartP) {
        return "SUBPART_P_PHARMACEUTICAL";
    }
    return "ORDINARY";
}
/** Missouri (MDNR) accumulation limits, 10 CSR 25-5.262. */
class MissouriRules extends FEDERAL_1.FederalOnlyRules {
    constructor() {
        super(...arguments);
        this.expectations = MO_EXPECTATIONS;
        this.accumulationThresholdExpectations = MO_ACCUMULATION_THRESHOLD_EXPECTATIONS;
        this.derivations = MO_DERIVATIONS;
    }
    satelliteClock(ctx) {
        // Missouri's customization is an alternative TO 40 CFR 262.15,
        // which only covers ordinary waste at SQGs and LQGs. Include
        // null (unknown generator status) so it has the stricter default
        if (effectiveMoWasteKind(ctx) === "ORDINARY" &&
            ["SQG", "LQG", null].includes(ctx.generatorStatus)) {
            // KNOWN GAPS -- see MO_SATELLITE_NOTE
            return MO_SATELLITE_STANDING_CLOCK;
        }
        return super.satelliteClock(ctx);
    }
}
exports.MissouriRules = MissouriRules;
//# sourceMappingURL=MO.js.map